// client/src/components/dashboard/Dashboard.js
import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  Grid,
  Paper,
  Box,
  CircularProgress,
  Card,
  CardContent,
  CardHeader,
  Divider
} from '@mui/material';
import { 
  School as SchoolIcon, 
  Person as PersonIcon 
} from '@mui/icons-material';
import { AuthContext } from '../../contexts/AuthContext';
import { LocaleContext } from '../../contexts/LocaleContext';
import facultyService from '../../services/facultyService';
import userService from '../../services/userService';

const Dashboard = () => {
  const { t, i18n } = useTranslation();
  const { user, isAdmin } = useContext(AuthContext);
  const { direction } = useContext(LocaleContext);
  const [stats, setStats] = useState({
    totalFaculties: 0,
    totalUsers: 0,
    totalAdmins: 0,
    totalFacultyUsers: 0,
    userFaculty: null
  });
  const [loading, setLoading] = useState(true);

  // Current language
  const lang = i18n.language;

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        
        // Load faculties
        const facultiesResponse = await facultyService.getFaculties();
        const faculties = facultiesResponse.data;
        
        // Load users
        const usersResponse = await userService.getUsers();
        const users = usersResponse.data;
        
        // Calculate stats
        const totalFaculties = faculties.length;
        const totalUsers = users.length;
        const totalAdmins = users.filter(u => u.role === 'admin').length;
        const totalFacultyUsers = users.filter(u => u.role === 'faculty').length;
        
        // Get current user's faculty
        let userFaculty = null;
        if (user.role === 'faculty' && user.faculty_id) {
          userFaculty = faculties.find(f => f.id === user.faculty_id);
        }
        
        setStats({
          totalFaculties,
          totalUsers,
          totalAdmins,
          totalFacultyUsers,
          userFaculty
        });
      } catch (error) {
        console.error('Error loading dashboard data', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadDashboardData();
  }, [user]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        {t('dashboard')}
      </Typography>
      
      <Box mb={4}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('welcome')}, {user.username}!
          </Typography>
          <Typography variant="body1">
            {t('dashboardIntro')}
          </Typography>
        </Paper>
      </Box>
      
      <Grid container spacing={3}>
        {/* Stats cards - only for admins */}
        {isAdmin() && (
          <>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <SchoolIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" component="div">
                      {t('faculties')}
                    </Typography>
                  </Box>
                  <Typography variant="h3" component="div" align="center">
                    {stats.totalFaculties}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PersonIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" component="div">
                      {t('totalUsers')}
                    </Typography>
                  </Box>
                  <Typography variant="h3" component="div" align="center">
                    {stats.totalUsers}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PersonIcon color="secondary" sx={{ mr: 1 }} />
                    <Typography variant="h6" component="div">
                      {t('adminUsers')}
                    </Typography>
                  </Box>
                  <Typography variant="h3" component="div" align="center">
                    {stats.totalAdmins}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PersonIcon color="info" sx={{ mr: 1 }} />
                    <Typography variant="h6" component="div">
                      {t('facultyUsers')}
                    </Typography>
                  </Box>
                  <Typography variant="h3" component="div" align="center">
                    {stats.totalFacultyUsers}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </>
        )}
        
        {/* Faculty Info - for faculty users */}
        {user.role === 'faculty' && stats.userFaculty && (
          <Grid item xs={12}>
            <Card>
              <CardHeader 
                title={t('yourFaculty')}
                subheader={stats.userFaculty[`name_${lang}`]}
              />
              <Divider />
              <CardContent>
                <Typography variant="body1">
                  {stats.userFaculty[`description_${lang}`] || t('noDescription')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Container>
  );
};

export default Dashboard;